import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@shared/authContext';
import { ClientModalProvider } from '@shared/clientModalContext';
import { canAccessAdvancedSettings } from '@shared/permissions';
import AppShell from './shell/AppShell';
import Login from './pages/Login';
import DispatchWorkspace from './features/dispatch/DispatchWorkspace';
import InvoicingWorkspace from './features/invoicing/InvoicingWorkspace';
import ManagementPage from './features/management/ManagementPage';
import Users from './pages/Users';
import Clients from './pages/Clients';
import ServiceCalls from './pages/ServiceCalls';
import Inventory from './pages/Inventory';
import InventoryLocations from './pages/InventoryLocations';
import PartsNeeded from './pages/PartsNeeded';
import Profile from './pages/Profile';
import AdvancedSettings from './pages/AdvancedSettings';
import TechScheduling from './pages/TechScheduling';
import Reports from './pages/Reports';
import LoadingSpinner from './components/LoadingSpinner';

function App() {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-secondary">
        <div className="max-w-md w-full card p-6 text-center">
          <h1 className="text-xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600 mb-6">
            You don't have permission to access the admin dashboard.
          </p>
          <button
            onClick={() => { window.location.href = `${window.location.origin}/tech`; }}
            className="btn btn-primary"
          >
            Go to Technician App
          </button>
        </div>
      </div>
    );
  }

  const canViewAdvancedSettings = canAccessAdvancedSettings(user);

  return (
    <ClientModalProvider>
      <AppShell>
        <Routes>
          <Route path="/" element={<Navigate to="/dispatch" replace />} />

          {/* Dispatch zone */}
          <Route path="/dispatch" element={<DispatchWorkspace />} />
          <Route path="/regular-service" element={<Navigate to="/dispatch" replace />} />
          <Route path="/tech-assignments" element={<Navigate to="/dispatch" replace />} />
          <Route path="/priority-service" element={<Navigate to="/dispatch" replace />} />
          <Route path="/schedule" element={<Navigate to="/dispatch" replace />} />
          <Route path="/map" element={<Navigate to="/dispatch" replace />} />
          <Route path="/parts-needed" element={<div className="hub-page"><PartsNeeded /></div>} />

          {/* Invoicing zone */}
          <Route path="/invoicing" element={<InvoicingWorkspace />} />
          <Route path="/service-calls" element={<div className="hub-page"><ServiceCalls /></div>} />

          {/* Management zone */}
          <Route path="/customers" element={<ManagementPage><Clients /></ManagementPage>} />
          <Route path="/clients" element={<Navigate to="/customers" replace />} />
          <Route path="/users" element={<ManagementPage><Users /></ManagementPage>} />
          <Route path="/tech-scheduling" element={<ManagementPage><TechScheduling /></ManagementPage>} />
          <Route path="/reports" element={<ManagementPage><Reports /></ManagementPage>} />
          <Route path="/monthly-roster" element={<Navigate to="/reports" replace />} />
          <Route path="/tech-metrics" element={<Navigate to="/reports" replace />} />
          <Route path="/service-history" element={<Navigate to="/reports" replace />} />
          <Route path="/inventory" element={<ManagementPage><Inventory /></ManagementPage>} />
          <Route path="/inventory-locations" element={<ManagementPage><InventoryLocations /></ManagementPage>} />
          <Route
            path="/settings"
            element={
              canViewAdvancedSettings ? (
                <ManagementPage><AdvancedSettings /></ManagementPage>
              ) : (
                <Navigate to="/dispatch" replace />
              )
            }
          />
          <Route path="/advanced-settings" element={<Navigate to="/settings" replace />} />

          <Route path="/profile" element={<div className="hub-page"><Profile /></div>} />
          <Route path="*" element={<Navigate to="/dispatch" replace />} />
        </Routes>
      </AppShell>
    </ClientModalProvider>
  );
}

export default App;
